import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const TimeRangeContext = createContext(null);

export const TimeRangeProvider = ({ children }) => {
  const [timeRange, setTimeRange] = useState(() => {
    const saved = localStorage.getItem('timeRange');
    return saved || '30d';
  });
  const [isReloading, setIsReloading] = useState(false);
  const [lastReload, setLastReload] = useState(new Date());

  // Persist time range selection
  useEffect(() => {
    localStorage.setItem('timeRange', timeRange);
  }, [timeRange]);

  // Calculate date range based on selected time range with validation
  const getDateRange = useCallback(() => {
    const end = new Date();
    let start = new Date();

    // Ensure we have valid dates
    if (!end || isNaN(end.getTime())) {
      end = new Date();
    }

    switch (timeRange) {
      case 'today':
        start = new Date(end);
        start.setHours(0, 0, 0, 0);
        break;
      case '7d':
        start = new Date(end);
        start.setDate(end.getDate() - 7);
        break;
      case '14d':
        start = new Date(end);
        start.setDate(end.getDate() - 14);
        break;
      case '30d':
        start = new Date(end);
        start.setDate(end.getDate() - 30);
        break;
      case 'month':
        start = new Date(end.getFullYear(), end.getMonth(), 1);
        break;
      case 'quarter':
        const currentQuarter = Math.floor(end.getMonth() / 3);
        start = new Date(end.getFullYear(), currentQuarter * 3, 1);
        break;
      case 'year':
        start = new Date(end.getFullYear(), 0, 1);
        break;
      case 'all':
        start = new Date(2020, 0, 1); // Set to your app's launch date
        break;
      default:
        start = new Date(end);
        start.setDate(end.getDate() - 30);
    }

    // Ensure we never return future dates or invalid dates
    const now = new Date();
    if (end > now || isNaN(end.getTime())) {
      end.setTime(now.getTime());
    }
    if (start > end || isNaN(start.getTime())) {
      start.setTime(end.getTime());
    }

    // Ensure start is not before app launch date
    const appLaunchDate = new Date(2020, 0, 1);
    if (start < appLaunchDate) {
      start = new Date(appLaunchDate);
    }

    return { start, end };
  }, [timeRange]);

  // Format date range for display with validation
  const formatDateRange = useCallback(() => {
    const { start, end } = getDateRange();

    // Validate dates before formatting
    if (!start || !end || isNaN(start.getTime()) || isNaN(end.getTime())) {
      return 'Invalid date range';
    }

    const formatDate = (date) => {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    };

    if (timeRange === 'today') {
      return formatDate(start);
    }

    return `${formatDate(start)} - ${formatDate(end)}`;
  }, [timeRange, getDateRange]);

  // Get time range in days for API calls with validation
  const getTimeRangeInDays = useCallback(() => {
    const { start, end } = getDateRange();

    if (!start || !end || isNaN(start.getTime()) || isNaN(end.getTime())) {
      return 30; // Default fallback
    }

    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return Math.max(1, diffDays); // Ensure at least 1 day
  }, [getDateRange]);

  // Reload data with validation
  const reloadData = useCallback(async () => {
    setIsReloading(true);
    try {
      // Simulate reload delay
      await new Promise(resolve => setTimeout(resolve, 500));
      setLastReload(new Date());
    } catch (error) {
      console.error('Error reloading data:', error);
    } finally {
      setIsReloading(false);
    }
  }, []);

  const value = {
    timeRange,
    setTimeRange,
    getDateRange,
    formatDateRange,
    getTimeRangeInDays,
    reloadData,
    isReloading,
    lastReload,
  };

  return (
    <TimeRangeContext.Provider value={value}>
      {children}
    </TimeRangeContext.Provider>
  );
};

export const useTimeRange = () => {
  const context = useContext(TimeRangeContext);
  if (!context) {
    throw new Error('useTimeRange must be used within a TimeRangeProvider');
  }
  return context;
}; 